import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { useDealer } from '../../context/DealerContext';
import { dealerApi, DealerBooking } from '../../services/dealerApi';
import Money from '../../components/dealer/Money';
import StatusChip from '../../components/dealer/StatusChip';
import { dealerErrorMessage, formatDate } from '../../utils/dealer';

/** Spec 085 (Flow J step 2, BR-24) — only the bookings this dealer made, newest first. */
export default function DealerBookingsPage() {
  const { t, lang } = useLanguage();
  const { token } = useDealer();
  const [rows, setRows] = useState<DealerBooking[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (next: string | null) => {
    if (!token) return;
    setLoading(true);
    try {
      const page = await dealerApi.listBookings(token, { cursor: next });
      setRows((prev) => (next ? [...prev, ...page.content] : page.content));
      setCursor(page.nextCursor);
      setHasMore(page.hasMore);
      setError(null);
    } catch (err) {
      setError(dealerErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => { load(null); }, [load]);

  return (
    <div className="dealer-page">
      <div className="dealer-page__header">
        <div>
          <h2>{t('dealer.bookings.title')}</h2>
          <p className="dealer-muted">{t('dealer.bookings.subtitle')}</p>
        </div>
        <Link className="btn btn-primary" to="/dealer/bookings/new">{t('dealer.bookings.new')}</Link>
      </div>
      {error && <div className="error-message">{error}</div>}
      {!loading && rows.length === 0 && !error && <p className="dealer-muted dealer-empty-row">{t('dealer.bookings.empty')}</p>}
      {rows.length > 0 && (
        <div className="dealer-table-wrap">
          <table className="dealer-table">
            <thead>
              <tr>
                <th>{t('dealer.bookings.date')}</th>
                <th>{t('dealer.bookings.service')}</th>
                <th>{t('dealer.bookings.garage')}</th>
                <th>{t('dealer.bookings.creditUsed')}</th>
                <th>{t('dealer.credits.status')}</th>
                <th aria-label={t('dealer.common.actions')}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.bookingId}>
                  <td>{formatDate(b.scheduledDate, lang)}</td>
                  <td>{b.serviceName || '—'}</td>
                  <td>{b.garageName || '—'}</td>
                  <td><Money value={b.creditAppliedAed} /></td>
                  <td><StatusChip status={b.orderStatus ?? b.status} /></td>
                  <td><Link to={`/dealer/bookings/${b.bookingId}`}>{t('dealer.common.view')}</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {hasMore && (
        <button type="button" className="btn btn-secondary" disabled={loading} onClick={() => load(cursor)}>
          {t('dealer.common.loadMore')}
        </button>
      )}
    </div>
  );
}
